import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { FbModal as Modal } from 'components/UI/Modal'
import Checkbox from 'components/Checkbox/checkbox'
import { MULTI_VALUE_SELECT_COLORS } from 'utils/constants'

export interface PickerOption {
  label: string
  value: number
  color?: string
}

interface AttributePickerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Modal title (e.g. "Add tag"). */
  title: string
  /** Noun used in the footer button ("Add 3 tags"). */
  itemNoun: string
  /** All options to choose from. */
  options: PickerOption[]
  /** Currently-selected ids — these are excluded from the picker list. */
  selectedIds: number[]
  /** Called once per id chosen when the user clicks the footer button. */
  onSelect: (id: number) => void
  /** Optional inline "Create new" handler — receives the typed search
   *  string, returns the id of the created option (or null on failure)
   *  for immediate addition. Mutually exclusive with onCreateExternal. */
  onCreate?: (value: string) => Promise<number | null>
  /** Optional external create handoff — receives the typed search
   *  string. The caller is responsible for opening a richer create
   *  modal and (if successful) adding the new id afterwards. The
   *  picker closes when this is invoked. */
  onCreateExternal?: (value: string) => void
}

/** Modal for staging multiple selections from a list of options, with
 *  an optional inline "Create new" affordance. The picker filters by
 *  search, excludes already-selected ids, and commits all staged
 *  selections at once via the footer button. */
export function AttributePickerModal({
  open,
  onOpenChange,
  title,
  itemNoun,
  options,
  selectedIds,
  onSelect,
  onCreate,
  onCreateExternal,
}: AttributePickerModalProps) {
  const [search, setSearch] = useState('')
  const [staged, setStaged] = useState<number[]>([])
  const [creating, setCreating] = useState(false)

  // Reset internal state every time the modal opens or closes so the
  // next open starts from a clean slate.
  const handleOpenChange = (next: boolean) => {
    if (next !== open) {
      setSearch('')
      setStaged([])
    }
    onOpenChange(next)
  }

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  // Available options = not already selected on the entity. Search is
  // case-insensitive and matches anywhere in the label.
  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase()
    return options
      .filter((o) => !selectedSet.has(o.value))
      .filter((o) => (q ? o.label.toLowerCase().includes(q) : true))
  }, [options, selectedSet, search])

  // Allow creating a new option when the search doesn't exactly match
  // any existing one (case-insensitive) and a create handler exists.
  const trimmedSearch = search.trim()
  const exactMatch = useMemo(
    () =>
      trimmedSearch.length > 0 &&
      options.some(
        (o) => o.label.toLowerCase() === trimmedSearch.toLowerCase()
      ),
    [options, trimmedSearch]
  )
  const canCreate =
    trimmedSearch.length > 0 &&
    !exactMatch &&
    (onCreate !== undefined || onCreateExternal !== undefined)

  const toggle = (id: number) => {
    setStaged((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    )
  }

  const handleCreate = async () => {
    if (!trimmedSearch) return
    if (onCreateExternal) {
      // Hand off to a richer create flow (e.g. CreateCampaignModalV2).
      onCreateExternal(trimmedSearch)
      return
    }
    if (!onCreate) return
    setCreating(true)
    const id = await onCreate(trimmedSearch)
    setCreating(false)
    if (id != null) {
      // Auto-stage the new option so the user just needs to confirm.
      setStaged((prev) => (prev.includes(id) ? prev : [...prev, id]))
      setSearch('')
    }
  }

  const handleConfirm = () => {
    staged.forEach((id) => onSelect(id))
    onOpenChange(false)
  }

  const paletteFor = (color?: string) =>
    MULTI_VALUE_SELECT_COLORS.find((c) => c.background === color) ??
    MULTI_VALUE_SELECT_COLORS[0]

  const stagedCount = staged.length
  const pluralSuffix = itemNoun.toLowerCase() === 'gpo' ? 's' : 's'
  const footerLabel =
    stagedCount === 0
      ? `Add ${itemNoun}${pluralSuffix}`
      : `Add ${stagedCount} ${itemNoun}${stagedCount === 1 ? '' : pluralSuffix}`

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      size="md"
      title={title}
      acceptButtonText={footerLabel}
      blockAccept={stagedCount === 0}
      onAccept={handleConfirm}
    >
      <div className="space-y-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          // Intercept Enter so it triggers Create (when there's no
          // exact match) instead of submitting FbModal's surrounding
          // <form> — which would close the picker with no selections.
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              if (canCreate) handleCreate()
            }
          }}
          placeholder={`Search or create a ${itemNoun.toLowerCase()}`}
          className="w-full h-9 px-3 text-sm bg-white border border-gray-300 rounded-md text-fg placeholder:text-gray-400 outline-none focus:border-gray-500 transition-colors"
          autoFocus
        />

        {canCreate && (
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded-md border border-dashed border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <Plus className="size-4 text-fg-secondary" />
            <span className="text-fg">
              Create{' '}
              <span className="font-medium">&quot;{trimmedSearch}&quot;</span>
            </span>
          </button>
        )}

        <div className="max-h-[320px] overflow-y-auto -mx-1 px-1">
          {filteredOptions.length === 0 && !canCreate && (
            <div className="py-8 text-center text-sm text-fg-secondary">
              {search.trim()
                ? `No ${itemNoun.toLowerCase()}s match "${search.trim()}".`
                : `No ${itemNoun.toLowerCase()}s available.`}
            </div>
          )}
          <ul className="space-y-1">
            {filteredOptions.map((option) => {
              const isStaged = staged.includes(option.value)
              const palette = paletteFor(option.color)
              return (
                <li key={option.value}>
                  <label className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors">
                    <Checkbox
                      checked={isStaged}
                      onChange={() => toggle(option.value)}
                      wrapperClassName="flex-shrink-0"
                    />
                    <span
                      className="inline-flex items-center px-2.5 h-6 rounded-full text-sm font-medium truncate max-w-[280px]"
                      style={{
                        backgroundColor: palette.background,
                        color: palette.text,
                      }}
                    >
                      {option.label}
                    </span>
                  </label>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </Modal>
  )
}
